// createdByUtils.js
//
// The QuickTale "Created By" field stores a single id that can point to EITHER
// a users.id OR an organizations.id. These helpers provide a single source of
// truth for (a) resolving that id into a display object and (b) formatting that
// object into a human-readable label.
//
// Usage:
//   const data = await resolveCreatedByData(createdBy);   // { ...doc, id, _sourceType }
//   const label = getCreatedByDisplay(data, createdBy);    // "Org Name" | "First Last /@user"
//
// Convenience:
//   const label = getCreatedByDisplayFromQT(quicktaleDetails);
//
// Note: `_sourceType` is a display-only tag ("user" | "organization"). It is
// never persisted back to Firestore.

import { getSingleUser } from "../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import { getSingleOrg } from "../modules/OrgProf/hook/Organizationhook";

/**
 * Determine whether a resolved createdByData object represents an organization.
 * Prefers the explicit _sourceType tag, falling back to a shape heuristic
 * (organizations have a `name` but no `firstName`).
 * @param {object|null} data
 * @returns {boolean}
 */
export const isOrganizationCreatedBy = (data) => {
  if (data == null) return false;
  if (data._sourceType === "organization") return true;
  if (data._sourceType === "user") return false;
  return !!(data.name && !data.firstName);
};

/**
 * Format a resolved createdByData object into a display label.
 * @param {object|null} createdByData - The resolved user or org object.
 * @param {string} [fallbackId] - Raw createdBy id used when nothing resolves.
 * @returns {string}
 */
export const getCreatedByDisplay = (createdByData, fallbackId = "") => {
  const data = createdByData;
  if (data == null) {

    return fallbackId || "";
  }
  // Organization: show the org name.
  if (isOrganizationCreatedBy(data)) {
    return data.name || fallbackId || "";
  }
  // User: "First Last /@userName" (or just the full name when no userName).
  const displayName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
  if (data.userName) {
    return displayName ? `${displayName} /@${data.userName}` : `/@${data.userName}`;
  }
  return displayName || fallbackId || "";
};

/**
 * Convenience wrapper for a QuickTale-shaped object that carries
 * `createdByData` (resolved) and `createdBy` (raw id).
 * @param {object} quicktaleDetails
 * @returns {string}
 */
export const getCreatedByDisplayFromQT = (quicktaleDetails) => {
  return getCreatedByDisplay(
    quicktaleDetails?.createdByData,
    quicktaleDetails?.createdBy || ""
  );
};

/**
 * Resolve a createdBy id into a display object. Tries the `users` collection
 * first, then falls back to `organizations`. Returns null if neither resolves.
 * The returned object is tagged with `_sourceType` for display purposes only.
 *
 * @param {string} createdById
 * @param {object[]} [organizationsData] - Optional pre-loaded organizations list
 *   (e.g. from a provider). When provided, the org fallback matches against this
 *   list first, avoiding a direct `organizations/{id}` read that can be blocked
 *   by Firestore rules for some admin roles.
 * @returns {Promise<object|null>}
 */
export const resolveCreatedByData = async (createdById, organizationsData = null) => {
  if (!createdById) return null;

  // 1) Try users first.
  try {
    const userDoc = await getSingleUser(createdById);
    if (userDoc?.exists?.() && userDoc?.data()) {
      return { ...userDoc.data(), id: createdById, _sourceType: "user" };
    }
  } catch (e) {
    console.warn("resolveCreatedByData: user lookup failed:", e?.message);
  }

  // 2a) Fall back to a pre-loaded organizations list, if provided. This avoids
  //     a direct organizations/{id} read (which some admin roles can't perform).
  if (Array.isArray(organizationsData) && organizationsData.length > 0) {
    const org = organizationsData.find((o) => o?.id === createdById);
    if (org) {
      return { ...org, id: createdById, _sourceType: "organization" };
    }
  }

  // 2b) Fall back to a direct organizations lookup.
  try {
    const orgDoc = await getSingleOrg(createdById);
    if (orgDoc?.exists?.() && orgDoc?.data()) {
      return { ...orgDoc.data(), id: createdById, _sourceType: "organization" };
    }
  } catch (e) {
    console.warn("resolveCreatedByData: organization lookup failed:", e?.message);
  }

  return null;
};


